import { AbstractGameService } from '../core/abstraction/AbstractGameService';
import {
  BONUS_AMOUNT_MULTIPLE, FUN_BALANCE_AMOUNT_VALUE,
  GAME_SCENE,
  PLAYING_GAME_MODE,
} from '../core/constats';

import { RealGameStrategy } from './gameServiceStrategies/RealGameStrategy';
import { DemoGameStrategy } from './gameServiceStrategies/DemoGameStrategy';
import { partnerService } from './helperService/PartnerService';
import {
  ACTIVE_ODD, BONUS_COST_AMOUNT,
  BONUS_RESPONSE_TOTAL_AMOUNT,
  FREE_SPINS_STATE,
  GAME_SCENE_STATE, HAS_BUY_BONUS, LAST_RESULTS_BONUS,
  MULTIPLIER_STATE,
  PRE_BONUS_CRYSTALS_STATE,
  PRE_BONUS_ID,
  PRE_BONUS_RE_SPINS_LEFT_STATE,
  SPIN_AMOUNT,
  SPIN_AMOUNT_ACTIVE,
} from '../stateManagment/gameState';
import { HistoryItem } from '../core/clases/HistoryItem';
import { userService } from './helperService/UserService';
import { historyHelperService } from './helperService/HistoryHelperService';
import { crystalTypes } from '../core/gameConstants';
import { bigWinService } from './helperService/BigWinService';
import { MESSAGE_TYPES, notificationHandler } from './NotificationHandler';

export class GameService extends AbstractGameService {
  constructor(services) {
    super();
    this.services = services;
    this._mode = PLAYING_GAME_MODE.DEMO;
  }

  set mode(mode) {
    this._mode = mode;
  }

  get mode() {
    return this._mode;
  }

  get isRealMode() {
    return this.mode === PLAYING_GAME_MODE.REAL;
  }

  /**
   * @name instance
   * @returns {AbstractGameService}
   */
  get instance() {
    return this.services[this._mode];
  }

  /**
   * @name spin
   * @param {Object} body
   * @param {number} body.partnerId
   * @param {string} body.currencyCode
   * @param {number} body.amount
   * @param {number} body.crystal
   * @param {Object} config
   * @param {string} config.v - APi version
   * @returns {*}
   */
  spin(body = {}, config = {}) {
    const spinBody = { ...this._createSpinBody(), ...body };
    const history = new HistoryItem({});
    if (!this.isRealMode) {
      userService.minusBalance(spinBody.amount);
    }
    return this.instance.spin(spinBody, config).then(res => {

      if(!res.errorMessage){
        notificationHandler.yourBetIsAccepted()
      }
      historyHelperService.addLastHistoryInQueue(history.update({
        amount: spinBody.amount,
        crystal: res.result,
        winAmount: res.winAmount,
        odd: res.odd,
      }));
      if (this.isRealMode) {
        userService.updateBalance(res.balance);
      }
      return res;
    });
  }

  /**
   * @name bonusSpin
   * @param {Object} body
   * @param {Object} config
   * @param {string} config.v - APi version
   * @returns {*}
   */
  bonusSpin(body = {}, config = {}) {
    return this.instance.bonusSpin({ ...this._createBonusSpinBody(), ...body }, config);
  }

  /**
   * @name preBonusSpin
   * @param {Object} body
   * @param {string} body.bonusId - for demo version
   * @param {Object} config
   * @param {string} config.v - APi version
   * @returns {Promise<{
   *   spin: {
   *     crystalInfo: {
   *       crystal: number,
   *       count: number,
   *     }
   *     freeSpins: number
   *     multiplier: number
   *   }
   *   remainingSpinCount: number
   * }>}
   */
  preBonusSpin(body = {}, config = {}) {
    return this.instance.preBonusSpin({ ...this._createPreBonusSpinBody(), ...body }, config);
  }

  /**
   * @name buyBonus
   * @param {Object} body
   * @param {number}
   * @param {number} body.amount
   * @param {string} body.currencyCode
   * @param {Object} body.buyBonusData
   * @param {Object} config
   * @param {string} config.v - APi version
   * @returns {*}
   */
  buyBonus(body = {}, config = {}) {
    const history = new HistoryItem({});
    const buyBonusBody = { ...this._createBuyBonusBody(), ...body };
    const amount = buyBonusBody.amount / BONUS_AMOUNT_MULTIPLE;
    BONUS_COST_AMOUNT.next(amount);
    if (!gameService.isRealMode) {
      userService.minusBalance(buyBonusBody.amount);
    }

    historyHelperService.addLastHistoryInQueue(history.update({
      amount,
      crystal: crystalTypes.crystal_gold,
    }));

    return this.instance.buyBonus(buyBonusBody, config).then(res => {
      return res;
    });
  }

  /**
   * @name getInitialState
   * @param {Object} params
   * @param {number} params.partnerId
   * @param {boolean} params.IsDemo
   * @param {Object} config
   * @param {string} config.v - APi version
   * @returns {Promise<InitialStateResponse>}
   */
  getInitialState(params = {}, config = {}) {
    return this.instance
      .getInitialState({ ...this._createInitialStateParams(), ...params }, config)
      .then(data => {
        if (!data) {
          throw new Error('invalidData')
        }
        if (data.hasOwnProperty('maxWinLimit')) {
          bigWinService.maxWinLimit = data.maxWinLimit;
        }
        if (data.gameConfiguration && data.gameConfiguration.hasBuyBonus === false) {
          HAS_BUY_BONUS.next(false);
        }
        partnerService.currency = data.currencyCode;
        SPIN_AMOUNT.next(data.quickBets);
        if (!this.isRealMode) {
          userService.updateBalance(FUN_BALANCE_AMOUNT_VALUE);
        } else {
          SPIN_AMOUNT.next(data.quickBets);
        }

        if (this._mode === PLAYING_GAME_MODE.REAL) {
          userService.updateBalance(data.balance);

          if (data.bonusSpinInfo) {
            const {
              multiplier,
              remainingSpinCount,
              winAmount,
              bonusData: {
                remainingPreBonusSpinCount,
                bonusInfo,
              },
              results,
            } = data.bonusSpinInfo;
            if (results) {
              LAST_RESULTS_BONUS.next(results.reverse());
            }

            if (data.spinInfo) {
              const { amount } = data.spinInfo;
              BONUS_COST_AMOUNT.next(amount);
            }

            MULTIPLIER_STATE.next(multiplier);
            FREE_SPINS_STATE.next(remainingSpinCount);
            PRE_BONUS_RE_SPINS_LEFT_STATE.next(remainingPreBonusSpinCount);
            BONUS_RESPONSE_TOTAL_AMOUNT.next(winAmount);
            PRE_BONUS_CRYSTALS_STATE.next(
              bonusInfo.reduce((acc, { crystal, count }) => {
                acc[crystal] = count;
                return acc;
              }, {}),
            );
            if (remainingPreBonusSpinCount > 0) {
              GAME_SCENE_STATE.next(GAME_SCENE.PRE_BONUS);
            } else {
              GAME_SCENE_STATE.next(GAME_SCENE.BONUS);
            }
          }
        }
        return data;
      });
  }

  getBonusLastState() {
    return this.instance
      .getInitialState({ ...this._createInitialStateParams() })
      .then(data => {
        if (data.bonusSpinInfo) {
          const {
            multiplier,
            remainingSpinCount,
            winAmount,
            bonusData: {
              remainingPreBonusSpinCount,
              bonusInfo,
            },
            results,
          } = data.bonusSpinInfo;
          if (results) {
            LAST_RESULTS_BONUS.next(results.reverse());
          }

          if (data.spinInfo) {
            const { amount } = data.spinInfo;
            BONUS_COST_AMOUNT.next(amount);
          }

          MULTIPLIER_STATE.next(multiplier);
          FREE_SPINS_STATE.next(remainingSpinCount);
          PRE_BONUS_RE_SPINS_LEFT_STATE.next(remainingPreBonusSpinCount);
          BONUS_RESPONSE_TOTAL_AMOUNT.next(winAmount);
          PRE_BONUS_CRYSTALS_STATE.next(
            bonusInfo.reduce((acc, { crystal, count }) => {
              acc[crystal] = count;
              return acc;
            }, {}),
          );
          return data.bonusSpinInfo;
        }
        return null;
      });
  }

  _createSpinBody() {
    return {
      partnerId: partnerService.partnerId,
      amount: SPIN_AMOUNT.getValue()[SPIN_AMOUNT_ACTIVE.getValue()],
      crystal: ACTIVE_ODD.getValue(),
      currencyCode: partnerService.currency,
    };
  }

  _createBuyBonusBody() {
    if (this._mode === PLAYING_GAME_MODE.DEMO) {
      return {
        partnerId: partnerService.partnerId,
        amount: SPIN_AMOUNT.getValue()[SPIN_AMOUNT_ACTIVE.getValue()] * BONUS_AMOUNT_MULTIPLE,
        currencyCode: partnerService.currency,
        buyBonusData: null,
      };
    }
    return {
      partnerId: partnerService.partnerId,
      amount: SPIN_AMOUNT.getValue()[SPIN_AMOUNT_ACTIVE.getValue()] * BONUS_AMOUNT_MULTIPLE,
      currencyCode: partnerService.currency,
      buyBonusData: null,
    };
  }

  _createPreBonusSpinBody() {
    if (this._mode === PLAYING_GAME_MODE.DEMO) {
      return { bonusId: PRE_BONUS_ID.getValue() };
    }
    return {};
  }

  _createBonusSpinBody() {
    if (this._mode === PLAYING_GAME_MODE.DEMO) {
      return { bonusId: PRE_BONUS_ID.getValue() };
    }
    return {};
  }

  _createInitialStateParams() {
    if (this._mode === PLAYING_GAME_MODE.DEMO) {
      return {
        partnerId: partnerService.partnerId,
        isDemo: true,
      };
    }
    return {
      partnerId: partnerService.partnerId,
      isDemo: false,
    };
  }
}

export const gameService = new GameService({
  [PLAYING_GAME_MODE.REAL]: new RealGameStrategy(),
  [PLAYING_GAME_MODE.DEMO]: new DemoGameStrategy(),
});
